import {
    AcceptIcon,
    AcceptToggle,
    PoolDiscardChangesModal,
    PoolLineClampTypography,
    PoolTooltip,
    PoolVirtualTable,
} from '@synergy/components-next';
import { AddUserIcon, DeleteIcon } from '@synergy/theme-next';
import { BonusSettlementSupplierStatus, CustomerBonusSettlementSupplier } from '@synergy/models';
import { Box, IconButton, Stack, TableCell, TableRow, Tooltip } from '@mui/material';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { useParams, useSearchParams } from 'react-router-dom';
import match from 'autosuggest-highlight/match';

import { BonusConditionTableCellFooter } from './components/bonus-condition-table-cell-footer';
import { BonusConditionTableCellHeader } from './components/bonus-condition-table-cell-header';
import { BonusConditionTableCellValue } from './components/bonus-condition-table-cell-value';
import { BonusSettlementDetailFormValues } from '../models/bonus-settlement-detail-form-values';
import { bestLocaleSelector } from '../../../common/selectors/locale-selectors';
import {
    bonusSettlementDetailReadOnlySelector,
    bonusSettlementDetailSelector,
} from './store/bonus-settlements-detail-selector';
import { getCalculatedBonus } from './utils/get-calculated-bonus';
import { removeCustomer } from './store/bonus-settlements-detail-actions';
import { useAppDispatch, useAppSelector } from '../../../store/store';

export const BonusSettlementDetailTable = () => {
    const bonusSettlementDetail = useAppSelector(bonusSettlementDetailSelector);
    const formikContext = useFormikContext<BonusSettlementDetailFormValues>();
    const defaultCurrency = useAppSelector(state => state.settings.currency);
    const locale = useAppSelector(bestLocaleSelector);
    const dispatch = useAppDispatch();
    const readOnly = useAppSelector(bonusSettlementDetailReadOnlySelector);
    const [searchParams] = useSearchParams();
    const [filteredSettlements, setFilteredSettlements] = useState<CustomerBonusSettlementSupplier[]>([]);
    const [pendingRecalculation, setPendingRecalculation] = useState<{
        conditionId: number;
        index: number;
        value: string | null;
    } | null>(null);
    const { id: supplierBonusSettlementId } = useParams<{ id: string }>();
    const prevFormValuesRef = useRef(formikContext.values);

    useEffect(() => {
        const isFinished = searchParams.get('isFinished');
        const search = searchParams.get('search') || '';

        if (!isFinished && !search) {
            setFilteredSettlements(formikContext.values.customerBonusSettlements);
            return;
        }

        const filtered = formikContext.values.customerBonusSettlements.filter(settlement => {
            if (search) {
                const nameMatches = match(settlement.customerName, search, {
                    requireMatchAll: true,
                    insideWords: true,
                });
                const idMatches = match(settlement.customerId.toString(), search);
                if (nameMatches.length === 0 && idMatches.length === 0) {
                    return false;
                }
            }

            if (isFinished !== null) {
                if (isFinished === 'true' && !settlement.isFinished) {
                    return false;
                }
                if (isFinished === 'false' && settlement.isFinished) {
                    return false;
                }
            }

            return true;
        });

        setFilteredSettlements(filtered);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, formikContext.values.customerBonusSettlements]);

    useEffect(() => {
        if (pendingRecalculation && formikContext.values !== prevFormValuesRef.current) {
            const { value, conditionId, index } = pendingRecalculation;
            const result = getCalculatedBonus({
                value,
                bonusConditionId: conditionId,
                bonusConditions: bonusSettlementDetail?.bonusConditions ?? [],
                customerBonusSettlement: formikContext.values.customerBonusSettlements[index],
            });

            formikContext.setFieldValue(`customerBonusSettlements.${index}.actualBonus`, result);
            setPendingRecalculation(null);
        }
        prevFormValuesRef.current = formikContext.values;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingRecalculation]);

    if (!bonusSettlementDetail) return null;

    const { bonusConditions } = bonusSettlementDetail;

    const canDeleteCustomer = [
        BonusSettlementSupplierStatus.Open,
        BonusSettlementSupplierStatus.Reclaimed,
        BonusSettlementSupplierStatus.PartiallySent,
    ].includes(bonusSettlementDetail.status);

    const handleRemoveCustomer = (customerId: number) => {
        if (supplierBonusSettlementId) {
            dispatch(
                removeCustomer({
                    supplierBonusSettlementId: +supplierBonusSettlementId,
                    customerId,
                })
            );
        }
    };

    return (
        <>
            <PoolDiscardChangesModal isDirty={formikContext.dirty} />
            <PoolVirtualTable
                data={filteredSettlements}
                fixedHeaderContent={() => (
                    <TableRow sx={{ background: theme => theme.palette.common.white }}>
                        <TableCell sx={{ maxWidth: 240 }}>
                            <FormattedMessage id="app.table.column.customerWitIdAndLocation" />
                        </TableCell>
                        {bonusConditions.map(condition => {
                            return (
                                <BonusConditionTableCellHeader
                                    key={condition.bonusCondition.id}
                                    bonusCondition={condition.bonusCondition}
                                    defaultCurrency={defaultCurrency}
                                    locale={locale}
                                />
                            );
                        })}
                        <TableCell sx={{ width: 130, textAlign: 'right' }}>
                            <FormattedMessage id="app.table.column.bonusCalculated" />
                        </TableCell>
                        <TableCell sx={{ width: 110, textAlign: 'center' }}>
                            <FormattedMessage id="app.label.column.done" />
                        </TableCell>
                        <TableCell sx={{ width: 40, textAlign: 'center' }} />
                    </TableRow>
                )}
                rowRenderer={customerBonusSettlement => {
                    if (!bonusSettlementDetail.customerBonusSettlements) return null;

                    const index = formikContext.values.customerBonusSettlements.findIndex(
                        settlement => settlement.id === customerBonusSettlement.id
                    );

                    return (
                        <Fragment key={index}>
                            <TableCell sx={{ maxWidth: 240 }}>
                                <Stack flexDirection="row" gap={2} alignItems={'center'}>
                                    {customerBonusSettlement?.isAddedManually && (
                                        <PoolTooltip
                                            title={
                                                <FormattedMessage id="app.bonusSettlements.supplierDetail.addCustomerManually" />
                                            }
                                        >
                                            <AddUserIcon color="primary" />
                                        </PoolTooltip>
                                    )}

                                    <Stack>
                                        <PoolLineClampTypography>
                                            {customerBonusSettlement.customerName}
                                        </PoolLineClampTypography>
                                        <Box typography="body2" color="grey.800">
                                            {customerBonusSettlement.customerId}
                                            {customerBonusSettlement.city ? `・${customerBonusSettlement.city}` : ''}
                                        </Box>
                                    </Stack>
                                </Stack>
                            </TableCell>
                            {bonusConditions.map(condition => {
                                const { id: conditionId } = condition.bonusCondition;
                                const bonusConditionValueIndex = formikContext.values.customerBonusSettlements[
                                    index
                                ]?.bonusConditionValues?.findIndex(bcv => bcv.bonusConditionId === conditionId);

                                const value =
                                    formikContext.values.customerBonusSettlements[index]?.bonusConditionValues[
                                        bonusConditionValueIndex
                                    ]?.value ?? 0;
                                const revenue =
                                    formikContext.values.customerBonusSettlements[index]?.bonusConditionValues[
                                        bonusConditionValueIndex
                                    ]?.revenue;

                                return (
                                    <BonusConditionTableCellValue
                                        key={condition.bonusCondition.id}
                                        rowIndex={index}
                                        bonusConditionValueIndex={bonusConditionValueIndex}
                                        readOnly={readOnly}
                                        bonusCondition={condition.bonusCondition}
                                        defaultCurrency={defaultCurrency}
                                        handleChange={value => setPendingRecalculation({ value, conditionId, index })}
                                        locale={locale}
                                        value={value}
                                        revenue={revenue}
                                    />
                                );
                            })}
                            <TableCell sx={{ width: 130, textAlign: 'right' }}>
                                <FormattedNumber
                                    value={formikContext.values.customerBonusSettlements[index]?.actualBonus ?? 0}
                                    currency={defaultCurrency}
                                    style="currency"
                                    minimumFractionDigits={2}
                                    maximumFractionDigits={2}
                                />
                            </TableCell>
                            <TableCell sx={{ width: 110, textAlign: 'center' }}>
                                <PoolTooltip
                                    title={
                                        <FormattedMessage
                                            id={
                                                formikContext.values.customerBonusSettlements[index]?.isFinished
                                                    ? 'app.bonusSettlements.supplierDetail.done'
                                                    : 'app.bonusSettlements.supplierDetail.open'
                                            }
                                        />
                                    }
                                >
                                    <AcceptToggle
                                        size="small"
                                        checked={formikContext.values.customerBonusSettlements[index]?.isFinished}
                                        onClick={() =>
                                            formikContext.setFieldValue(
                                                `customerBonusSettlements.${index}.isFinished`,
                                                !customerBonusSettlement.isFinished
                                            )
                                        }
                                        disabled={readOnly}
                                    >
                                        <AcceptIcon sx={{ fontSize: 20 }} />
                                    </AcceptToggle>
                                </PoolTooltip>
                            </TableCell>
                            <TableCell>
                                {canDeleteCustomer && customerBonusSettlement?.isAddedManually && (
                                    <Tooltip
                                        title={<FormattedMessage id="app.bonusSettlements.supplierDetail.table.edit" />}
                                        placement="top"
                                    >
                                        <IconButton
                                            color="dark"
                                            onClick={() =>
                                                handleRemoveCustomer(
                                                    formikContext.values.customerBonusSettlements[index]?.id
                                                )
                                            }
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </TableCell>
                        </Fragment>
                    );
                }}
                fixedFooterContent={() => (
                    <TableRow>
                        <TableCell>
                            <FormattedMessage id="app.bonusSettlements.supplierDetail.total" />
                        </TableCell>
                        {bonusConditions.map(condition => {
                            let value = 0;
                            let totalRevenue = 0;

                            for (const settlement of formikContext.values.customerBonusSettlements) {
                                const conditionValue = settlement.bonusConditionValues.find(
                                    bcv => bcv.bonusConditionId === condition.bonusCondition.id
                                );

                                if (conditionValue) {
                                    value += conditionValue.value ?? 0;
                                    totalRevenue += conditionValue.revenue ?? 0;
                                }
                            }

                            return (
                                <BonusConditionTableCellFooter
                                    key={condition.bonusCondition.id}
                                    bonusCondition={condition.bonusCondition}
                                    revenue={totalRevenue}
                                    defaultCurrency={defaultCurrency}
                                    locale={locale}
                                    value={value}
                                />
                            );
                        })}
                        <TableCell sx={{ width: 130, textAlign: 'right' }}>
                            <FormattedNumber
                                value={formikContext.values.customerBonusSettlements.reduce(
                                    (sum, customerBonusSettlement) => sum + (customerBonusSettlement.actualBonus || 0),
                                    0
                                )}
                                currency={defaultCurrency}
                                style="currency"
                                minimumFractionDigits={2}
                                maximumFractionDigits={2}
                            />
                        </TableCell>
                        <TableCell sx={{ width: 110, textAlign: 'center' }}>
                            <FormattedMessage
                                id="app.bonusSettlements.supplierDetail.totalFinished"
                                values={{
                                    finished: formikContext.values.customerBonusSettlements.filter(
                                        customerBonusSettlement => customerBonusSettlement.isFinished
                                    ).length,
                                    total: formikContext.values.customerBonusSettlements.length,
                                }}
                            />
                        </TableCell>
                        <TableCell />
                    </TableRow>
                )}
            />
        </>
    );
};
